// In LoginPage.tsx
import { useEffect, useState } from 'react';
import { Eye, EyeOff, ArrowRight, ArrowLeft, Lock, Mail } from 'lucide-react';
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from '../../contexts/AuthContext';
import imgImage1 from "../../assets/0aec2039a323dfae679995f97066353c7b72b5eb.png";
import { loginSchema, type LoginFormData } from '../../validation/auth';

export default function LoginPage() {
  const { isAuthenticated, login, siteData: data } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState<LoginFormData>({
    email: '',
    password: '',
    rememberMe: false
  });
  const [errors, setErrors] = useState<Partial<Record<keyof LoginFormData, string>>>({});

  const [showPassword, setShowPassword] = useState(false);
  const { isLoading } = useAuth();

  useEffect(() => {
    const savedCredentials = localStorage.getItem('rememberedCredentials');
    if (savedCredentials) {
      const { email, password } = JSON.parse(savedCredentials);
      setFormData(prev => ({
        ...prev,
        email,
        password,
        rememberMe: true
      }));
    }
    if (isAuthenticated) {
      navigate('/admin/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value, type, checked } = e.target;

    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    setErrors(prev => ({
      ...prev,
      [name]: "",
    }));
  };


  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = loginSchema.safeParse(formData);
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.issues.forEach(issue => {
        if (issue.path.length > 0 && typeof issue.path[0] === 'string') {
          fieldErrors[issue.path[0]] = issue.message;
        }
      });
      setErrors(fieldErrors);
      return;
    }
    setErrors({}); // clear errors if valid
    try {
      await login(formData.email, formData.password, formData.rememberMe);
    } catch (error: any) {
      console.log(error.message || 'Login failed. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-[#020b12] flex items-center justify-center p-4 font-outfit relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-cyan-500/20 rounded-full blur-[128px] pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-500/20 rounded-full blur-[128px] pointer-events-none" />

      <div className="bg-[#0B1623]/80 backdrop-blur-xl border border-white/10 rounded-3xl shadow-2xl w-full max-w-md overflow-hidden relative z-10">
        <div className="p-8 sm:p-10">
          <div className="text-center mb-8">
            <Link to={'/'} className="w-16 h-16 bg-linear-to-br from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-cyan-500/20">
              <img src={data ? data?.headerLogo : imgImage1} alt="Logo" className="w-12 h-16 object-contain drop-shadow-md" />
            </Link>
            <h1 className="text-2xl font-bold text-white">Welcome Back</h1>
            <p className="text-slate-400 mt-2 text-sm">Enter your credentials to access the admin panel.</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-5">
            <div className="space-y-2">
              <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider ml-1">Email Address</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`w-full pl-12 pr-4 py-3.5 bg-black/20 border ${errors.email ? 'border-red-500' : 'border-white/10'
                    } rounded-xl focus:outline-none focus:border-cyan-500 text-white placeholder:text-slate-600 transition-all`}
                  placeholder="admin@akaram.no"
                />
                {errors.email && (
                  <p className="text-red-400 text-xs ml-1">{errors.email}</p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider ml-1">Password</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className="w-full pl-12 pr-12 py-3.5 bg-black/20 border border-white/10 rounded-xl focus:outline-none focus:border-cyan-500 text-white placeholder:text-slate-600 transition-all"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white transition-colors"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
                {errors.password && (
                  <p className="text-red-400 text-xs ml-1">{errors.password}</p>
                )}

              </div>
            </div>

            <div className="flex items-center justify-between text-xs">
              <label className="flex items-center gap-2 text-slate-400 cursor-pointer hover:text-slate-300">
                <input
                  type="checkbox"
                  name="rememberMe"
                  checked={formData.rememberMe}
                  onChange={handleChange}
                  className="rounded border-white/10 bg-white/5 text-cyan-500 focus:ring-0 focus:ring-offset-0"
                />
                Remember me
              </label>
              <Link to="/forgot-password" className="text-cyan-400 hover:text-cyan-300 font-medium">Forgot Password?</Link>
            </div>
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-linear-to-r from-cyan-600 to-blue-600 text-white font-semibold py-3.5 rounded-xl hover:from-cyan-500 hover:to-blue-500 transition-all shadow-lg shadow-cyan-900/20 flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed group"
            >
              {isLoading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Signing In...
                </>
              ) : (
                <>
                  Sign In Dashboard
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </>
              )}
            </button>
            <div className="text-center mt-6">
              <Link 
                to="/" 
                className="inline-flex items-center text-sm text-slate-400 hover:text-cyan-400 transition-colors"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Back to site
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}